import { useParams } from "@tanstack/solid-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { For, Show, createSignal } from "solid-js";

import { AgentsPanel } from "~/components/AgentsPanel";
import { AISettingsPanel } from "~/components/AISettingsPanel";
import { AppShell } from "~/components/AppShell";
import { AuditLogPanel } from "~/components/AuditLogPanel";
import { NotificationPreferencesPanel } from "~/components/NotificationPreferencesPanel";
import { CustomFieldsPanel } from "~/components/CustomFieldsPanel";
import { CustomObjectsPanel } from "~/components/CustomObjectsPanel";
import { PipelinesPanel } from "~/components/PipelinesPanel";
import { SubscriptionPanel } from "~/components/SubscriptionPanel";
import { apiRequest } from "~/lib/api";

type RoleItem = {
  id: string;
  key: string;
  name: string;
  level: string;
  description: string | null;
};

type MembershipItem = {
  id: string;
  workspace_id: string;
  subject_kind: "user" | "group";
  subject_id: string;
  role_id: string;
  role_key: string | null;
  role_level: string | null;
  status: string;
  joined_at: string | null;
  revoked_at: string | null;
  created_at: string;
  version: number;
};

type MembershipListPage = { items: MembershipItem[] };

export function WorkspaceSettingsPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };
  const qc = useQueryClient();

  const roles = useQuery(() => ({
    queryKey: ["workspace", params().wsKey, "roles"],
    queryFn: () => apiRequest<RoleItem[]>(`/v1/workspaces/${params().wsKey}/roles`),
  }));

  const memberships = useQuery(() => ({
    queryKey: ["workspace", params().wsKey, "memberships"],
    queryFn: () => apiRequest<MembershipListPage>(`/v1/workspaces/${params().wsKey}/memberships`),
  }));

  const inviteMutation = useMutation(() => ({
    mutationFn: (body: {
      subject_kind: "user" | "group";
      user_email?: string;
      subject_id?: string;
      role_key: string;
    }) =>
      apiRequest<MembershipItem>(`/v1/workspaces/${params().wsKey}/memberships`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace", params().wsKey, "memberships"] });
    },
  }));

  const revokeMutation = useMutation(() => ({
    mutationFn: (args: { membershipId: string; version: number }) =>
      apiRequest<MembershipItem>(
        `/v1/workspaces/${params().wsKey}/memberships/${args.membershipId}`,
        { method: "DELETE", ifMatch: args.version },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace", params().wsKey, "memberships"] });
    },
  }));

  const [showInvite, setShowInvite] = createSignal(false);
  const [inviteError, setInviteError] = createSignal<string | null>(null);

  const handleInvite = async (e: SubmitEvent) => {
    e.preventDefault();
    setInviteError(null);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const kind = String(data.get("subject_kind")) as "user" | "group";
    const body: {
      subject_kind: "user" | "group";
      user_email?: string;
      subject_id?: string;
      role_key: string;
    } = {
      subject_kind: kind,
      role_key: String(data.get("role_key") ?? "member"),
    };
    if (kind === "user") {
      const email = String(data.get("user_email") ?? "").trim();
      if (!email) {
        setInviteError("Email required");
        return;
      }
      body.user_email = email;
    } else {
      const gid = String(data.get("subject_id") ?? "").trim();
      if (!gid) {
        setInviteError("Group ID required");
        return;
      }
      body.subject_id = gid;
    }
    try {
      await inviteMutation.mutateAsync(body);
      setShowInvite(false);
    } catch (err) {
      setInviteError((err as Error).message);
    }
  };

  return (
    <AppShell
      wsKey={params().wsKey}
      pageTitle="Workspace settings"
      rightSlot={
        <button class="btn btn-primary btn-sm" onClick={() => setShowInvite(true)}>
          Invite
        </button>
      }
    >
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div class="card bg-base-100 shadow-sm lg:col-span-2">
          <div class="card-body">
            <h3 class="card-title text-base">Members</h3>
            <Show when={memberships.isLoading}>
              <div role="status" aria-live="polite" class="text-center py-6 opacity-60">
                Loading…
              </div>
            </Show>
            <Show when={memberships.data}>
              {(data) => (
                <Show
                  when={data().items.length > 0}
                  fallback={
                    <div class="text-center py-8 opacity-60 text-sm">
                      No memberships yet. Use Invite to add a user or group.
                    </div>
                  }
                >
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Joined</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      <For each={data().items}>
                        {(m) => (
                          <tr class="hover">
                            <td>
                              <span class="badge badge-ghost badge-sm mr-2">{m.subject_kind}</span>
                              <code class="text-xs">{m.subject_id}</code>
                            </td>
                            <td>
                              <span class="badge badge-outline badge-sm">{m.role_key ?? "?"}</span>
                            </td>
                            <td>
                              <span
                                class={`badge badge-sm ${
                                  m.status === "active"
                                    ? "badge-success"
                                    : m.status === "suspended"
                                      ? "badge-warning"
                                      : "badge-ghost"
                                }`}
                              >
                                {m.status}
                              </span>
                            </td>
                            <td class="text-xs opacity-60">
                              {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : "—"}
                            </td>
                            <td>
                              <Show when={m.status !== "revoked"}>
                                <button
                                  class="btn btn-ghost btn-xs text-error"
                                  aria-label={`Revoke membership for ${m.subject_kind} ${m.subject_id}`}
                                  onClick={() =>
                                    revokeMutation.mutate({
                                      membershipId: m.id,
                                      version: m.version,
                                    })
                                  }
                                >
                                  Revoke
                                </button>
                              </Show>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Show>
              )}
            </Show>
          </div>
        </div>

        <div class="card bg-base-100 shadow-sm">
          <div class="card-body">
            <h3 class="card-title text-base">Roles</h3>
            <Show when={roles.data}>
              {(data) => (
                <ul class="flex flex-col gap-2">
                  <For each={data()}>
                    {(r) => (
                      <li>
                        <div class="font-medium">{r.name}</div>
                        <div class="text-xs opacity-60">
                          {r.key} · level: {r.level}
                        </div>
                        <Show when={r.description}>
                          <div class="text-xs opacity-70">{r.description}</div>
                        </Show>
                      </li>
                    )}
                  </For>
                </ul>
              )}
            </Show>
          </div>
        </div>
      </div>

      <div class="mt-4">
        <PipelinesPanel wsKey={() => params().wsKey} />
      </div>

      <div class="mt-4">
        <CustomFieldsPanel wsKey={() => params().wsKey} />
      </div>

      <div class="mt-4">
        <CustomObjectsPanel wsKey={() => params().wsKey} />
      </div>

      <div class="mt-4">
        <SubscriptionPanel wsId={() => params().wsKey} />
      </div>

      <div class="mt-4">
        <AISettingsPanel wsKey={() => params().wsKey} />
      </div>

      <div class="mt-4">
        <AgentsPanel wsKey={() => params().wsKey} />
      </div>

      <div class="mt-4">
        <NotificationPreferencesPanel wsKey={() => params().wsKey} />
      </div>

      <div class="mt-4">
        <AuditLogPanel wsKey={() => params().wsKey} />
      </div>

      <Show when={showInvite()}>
        <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="invite-title">
          <div class="modal-box">
            <h3 id="invite-title" class="font-bold text-lg mb-4">
              Invite to workspace
            </h3>
            <form onSubmit={handleInvite} class="flex flex-col gap-3">
              <select name="subject_kind" class="select select-bordered" aria-label="Subject kind">
                <option value="user">User</option>
                <option value="group">Group</option>
              </select>
              <input
                name="user_email"
                type="email"
                class="input input-bordered"
                placeholder="user@acme.example (for users)"
                aria-label="User email"
              />
              <input
                name="subject_id"
                class="input input-bordered"
                placeholder="grp_… (for groups)"
                aria-label="Group ID"
              />
              <select name="role_key" class="select select-bordered" aria-label="Role">
                <option value="admin">Admin</option>
                <option value="member" selected>
                  Member
                </option>
                <option value="viewer">Viewer</option>
              </select>
              <Show when={inviteError()}>
                <div class="alert alert-error text-sm">{inviteError()}</div>
              </Show>
              <div class="modal-action">
                <button type="button" class="btn btn-ghost" onClick={() => setShowInvite(false)}>
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" disabled={inviteMutation.isPending}>
                  {inviteMutation.isPending ? "Inviting…" : "Invite"}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </Show>
    </AppShell>
  );
}
